import { useQueries } from '@tanstack/react-query';
import { useAuth } from '../../../auth/useAuth';
import { knowledgeService } from '../../../services/KnowledgeService';
import type { KnowledgeBaseWithIdentity } from '../../../types/knowledge';
import { canCreateChatbotFromKnowledgeBase } from '../knowledgeDatabasePermissions';
import { knowledgeQueryKeys } from '../knowledgeQueryKeys';
import { useKnowledgeDatabasesQuery } from '../queries/useKnowledgeDatabasesQuery';

/**
 * Knowledge databases the current user may attach when creating a chatbot
 * (requires EDITOR or ADMIN membership on that KDB).
 */
export function useKnowledgeDatabasesEligibleForChatbotCreate(options?: {
  enabled?: boolean;
}): {
  databases: KnowledgeBaseWithIdentity[];
  isLoading: boolean;
  isError: boolean;
} {
  const { user } = useAuth();
  const databasesQuery = useKnowledgeDatabasesQuery();
  const databases = databasesQuery.data ?? [];
  const isMembersEnabled =
    (options?.enabled ?? true) && Boolean(user) && databases.length > 0;

  const membersQueries = useQueries({
    queries: databases.map((database) => ({
      queryKey: knowledgeQueryKeys.members(database.id),
      queryFn: () => knowledgeService.listMembers(database.id),
      enabled: isMembersEnabled,
    })),
  });

  const eligibleDatabases =
    !user?.id || !(options?.enabled ?? true)
      ? []
      : databases.filter((_database, index) => {
          const members = membersQueries[index]?.data;
          if (!members) {
            return false;
          }
          const role = members.find(
            (member) => member.userId === user.id,
          )?.role;
          return canCreateChatbotFromKnowledgeBase(role);
        });

  const isLoading =
    (options?.enabled ?? true) &&
    (databasesQuery.isLoading ||
      (isMembersEnabled && membersQueries.some((query) => query.isLoading)));

  const isError =
    (options?.enabled ?? true) &&
    (databasesQuery.isError || membersQueries.some((query) => query.isError));

  return {
    databases: eligibleDatabases,
    isLoading,
    isError,
  };
}
